import { useState, useMemo } from 'react'
import { DeviceFilters } from '@/components/DeviceFilters'
import { Device } from '@/models/device'

export function useDeviceFilters(devices: Device[]) {
  const [filters, setFilters] = useState<DeviceFilters>({
    serialNumber: '',
    imei: '',
    phoneNumber: '',
    simSerialNumber: '',
    simProvider: [],
    customFields: {},
    type: [],
    configurationId: [],
    firmware: [],
    configurationStatus: [],
    firmwareStatus: [],
    dateAddedFrom: '',
    dateAddedTo: ''
  })

  const handleFilterChange = (key: keyof DeviceFilters, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const onCustomFieldFilterChange = (fieldId: string, selectedValues: string[]) => {
    setFilters(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [fieldId]: selectedValues
      }
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      serialNumber: '',
      imei: '',
      phoneNumber: '',
      simSerialNumber: '',
      simProvider: [],
      customFields: {},
      type: [],
      configurationId: [],
      firmware: [],
      configurationStatus: [],
      firmwareStatus: [],
      dateAddedFrom: '',
      dateAddedTo: ''
    })
  }

  // Filter devices based on current filters
  const filteredDevices = useMemo(() => {
    // Helper to check if a device field matches any of the comma-separated search terms
    const matchesAnyTerm = (deviceValue: string | undefined | null, filterValue: string): boolean => {
      if (!deviceValue) return false
      const searchTerms = filterValue.split(',').map(term => term.trim().toLowerCase()).filter(term => term.length > 0)
      return searchTerms.some(term => deviceValue.toLowerCase().includes(term))
    }

    return devices.filter(device => {

      // Text-based filters (case insensitive partial match)
      if (filters.serialNumber && !matchesAnyTerm(device.serialNumber, filters.serialNumber)) return false
      if (filters.imei && !matchesAnyTerm(device.imei, filters.imei)) return false
      if (filters.phoneNumber && !matchesAnyTerm(device.phoneNumber, filters.phoneNumber)) return false
      if (filters.simSerialNumber && !matchesAnyTerm(device.simSerialNumber, filters.simSerialNumber)) return false

      // Multi-select dropdown filters
      if (filters.simProvider.length > 0 && !filters.simProvider.includes(device.simProviderName || '')) return false
      
      const activeFilterIds = Object.keys(filters.customFields).filter(
        (id) => filters.customFields[id].length > 0
      );
      
      if (activeFilterIds.length > 0) {
        const matchesAllFilters = activeFilterIds.every((fieldId) => {
          const selectedOptions = filters.customFields[fieldId];
        
          const deviceValuesForThisField = device.subCustomFields
            ?.filter(v => v.parentCustomFieldId === fieldId)
            .map(v => v.name) || [];

          return deviceValuesForThisField.some(val => selectedOptions.includes(val));
        });
      
        if (!matchesAllFilters) return false;
      }

      if (filters.type.length > 0 && !filters.type.includes(device.type || '')) return false
      if (filters.configurationId.length > 0 && !filters.configurationId.includes(device.configurationId || '')) return false
      if (filters.firmware.length > 0 && !filters.firmware.includes(device.firmware || '')) return false
      if (filters.configurationStatus.length > 0 && !filters.configurationStatus.includes(device.configurationStatus || '')) return false
      if (filters.firmwareStatus.length > 0 && !filters.firmwareStatus.includes(device.firmwareStatus || '')) return false

      // Date filters
      if (filters.dateAddedFrom) {
        const deviceDate = new Date(device.dateAddedUtc)
        const fromDate = new Date(filters.dateAddedFrom)
        if (deviceDate < fromDate) return false
      }
      if (filters.dateAddedTo) {
        const deviceDate = new Date(device.dateAddedUtc)
        const toDate = new Date(filters.dateAddedTo)
        toDate.setHours(23, 59, 59, 999) // Include full day
        if (deviceDate > toDate) return false
      }

      return true
    })
  }, [devices, filters])

  return {
    filters,
    filteredDevices,
    onCustomFieldFilterChange,
    handleFilterChange,
    clearAllFilters
  }
}